import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAuth } from '@/hooks/useAuth';
import { useClerk } from '@clerk/clerk-react';
import { useUser } from '@/clerk.config';
import { useDispatch } from 'react-redux';
import { setUser, setLoadingProfile } from '@/store/slices/auth';
import { apiService } from '@/api/apiConfig';
import toast from 'react-hot-toast';

interface AdminRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children, redirectTo = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const clerkUser = useUser().user;
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isLoadingProfile = useAppSelector((state) => state.auth.isLoadingProfile);
  const { signOut } = useClerk();
  const dispatch = useDispatch();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState(false);

  useEffect(() => {
    const checkSessionPersistence = async () => {
      if (loading) return;

      if (user) {
        // Check if session should persist based on remember me preference
        const hasRememberMe = localStorage.getItem('clerkAuthSession') === 'true';
        const hasSessionAuth = sessionStorage.getItem('clerkAuthSession') === 'true';

        if (!hasRememberMe && !hasSessionAuth) {
          // User is signed in but no valid auth flag exists
          // This means they logged in without "Remember Me" and opened a new tab
          console.log('Session persistence check failed, signing out...');
          try {
            // Clear any remaining flags
            localStorage.removeItem('clerkAuthSession');
            localStorage.removeItem('rememberMe');
            sessionStorage.removeItem('clerkAuthSession');

            await signOut();
            setShouldRedirectToLogin(true);
          } catch (error) {
            console.error('Error signing out:', error);
          }
        }
      }

      setIsCheckingSession(false);
    };

    checkSessionPersistence();
  }, [user, loading, signOut]);

  // Fetch user profile data when user is available but currentUser is not set
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (clerkUser && !currentUser && !isLoadingProfile) {
        try {
          console.log('AdminRoute: Fetching user profile for:', clerkUser.id);

          // Set loading state to true
          dispatch(setLoadingProfile(true));

          // Fetch profile data from backend
          const { data: profileData } = await apiService.request<any>({
            method: 'GET',
            url: `/profile/clerk/${clerkUser.id}`
          });

          console.log('AdminRoute: profileData', profileData);

          // Dispatch user data to store (this will also set loading to false)
          dispatch(
            setUser({
              firstName: profileData?.firstName,
              lastName: profileData?.lastName,
              name: profileData?.firstName + ' ' + (profileData?.lastName || ''),
              email: profileData?.email,
              phoneNumber: profileData?.phoneNumber,
              photoURL: profileData?.profilePicUrl,
              id: profileData?.id,
              fid: profileData?.firebaseUid,
              username: profileData?.username,
              roles: profileData?.roles || [],
              status: profileData?.status || 'ACTIVE',
              unregisterDate: profileData?.unregisterDate || null,
              hourlyRate: profileData?.hourlyRate ?? 0
            })
          );
        } catch (error) {
          console.error('AdminRoute: Failed to fetch user profile:', error);
          toast.error('Failed to load user profile');
          // Set loading to false on error
          dispatch(setLoadingProfile(false));
        }
      }
    };

    fetchUserProfile();
  }, [clerkUser, currentUser, isLoadingProfile, dispatch]);

  // Show nothing while checking session persistence or loading profile
  if (loading || isCheckingSession || (clerkUser && !currentUser && isLoadingProfile)) {
    return null;
  }

  // Redirect to login if session persistence check failed
  if (shouldRedirectToLogin || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin - only check after profile is loaded
  if (currentUser && !currentUser?.roles?.includes('ADMIN')) {
    console.log('currentUser AdminRoute', currentUser);
    return <Navigate to={redirectTo} replace />;
  }

  // If profile is not loaded yet but user exists, wait
  if (clerkUser && !currentUser) {
    return null;
  }

  return <>{children}</>;
};

export default AdminRoute;
